import OrderDTO from "@/dto/order/OrderDTO";
import { cloneDeep } from "lodash";
import { computed, observable, makeObservable } from "mobx";
import skuPropertiesFilterApi from "@/services/api/setting/skuPropertiesFilter";
import { escapeRegExp } from "@/utils/RegExp";

/**
 * @see #EditSkuPropertiesFilterModal 组件配置相关
 */
const regexTools = [
    {
        keyword: "[任意字符]",
        regex: ".*?",
    },
];

/**
 * 关键词中是否包含正则表达式工具
 */
function hasRegexTool(keyword: string) {
    if (!keyword) {
        return false;
    }

    for (let i = 0; i < regexTools.length; i++) {
        const regexTool = regexTools[i];

        // 如果关键词跟正则表达式工具相同，则跳过
        if (keyword.trim() === regexTool.keyword) {
            continue;
        }

        if (keyword.includes(regexTool.keyword)) {
            return true;
        }
    }

    return false;
}

function convertRegexTool(keyword: string) {
    // 1. escape 关键词
    let escapeString = escapeRegExp(keyword);

    for (let i = 0; i < regexTools.length; i++) {
        const regexTool = regexTools[i];

        // 此处因为第1条注释，所以需要再次 escape
        const escapeKeyword = escapeRegExp(regexTool.keyword);

        escapeString = escapeString.replaceAll(escapeKeyword, regexTool.regex);
    }

    return new RegExp(escapeString, "g");
}

enum FilterKeywordExecutorType {
    STRING,
    REGEX,
}

type FilterKeywordExecutor =
    | {
          type: FilterKeywordExecutorType;

          value: RegExp;
      }
    | {
          type: FilterKeywordExecutorType.STRING;

          value: string;
      };

class SkuPropertiesFilterStore {
    constructor() {
        makeObservable(this, {
            filterKeywords: observable,
            filterKeywordExecutors: computed,
        });
    }

    filterKeywords: string[] = [];

    get filterKeywordExecutors(): FilterKeywordExecutor[] {
        const executors: FilterKeywordExecutor[] = [];

        for (let i = 0; i < this.filterKeywords.length; i++) {
            const filterKeyword = this.filterKeywords[i];

            if (hasRegexTool(filterKeyword)) {
                try {
                    executors.push({
                        type: FilterKeywordExecutorType.REGEX,
                        value: convertRegexTool(filterKeyword),
                    });
                } catch (error) {
                    console.error("convert regex error", filterKeyword);
                }
            } else {
                executors.push({
                    type: FilterKeywordExecutorType.STRING,
                    value: filterKeyword,
                });
            }
        }

        return executors;
    }

    loadFilterKeywords = async () => {
        this.filterKeywords = await skuPropertiesFilterApi.getCache();
    };

    process = (order: OrderDTO) => {
        if (!this.filterKeywords || !order.orderItems) {
            return order;
        }

        const newOrder = cloneDeep(order);

        newOrder.orderItems.forEach((orderItem) => {
            let skuProperties = orderItem.localSkuProperties || orderItem.skuProperties;

            if (!skuProperties) {
                return;
            }

            skuProperties = this.filterText(skuProperties);

            if (orderItem.localSkuProperties) {
                orderItem.localSkuProperties = skuProperties;
            } else {
                orderItem.skuProperties = skuProperties;
            }
        });

        return newOrder;
    };

    filterText = (strText: string) => {
        if (!strText) {
            console.warn("[SkuPropertiesFilterStore filterText]:empty strText");

            return "";
        }

        this.filterKeywordExecutors.forEach((executor) => {
            switch (executor.type) {
                case FilterKeywordExecutorType.REGEX: {
                    try {
                        strText = strText.replaceAll(executor.value, "");
                    } catch (error) {
                        console.error("exec filter keyword regex error", executor.value);
                    }
                    break;
                }
                case FilterKeywordExecutorType.STRING: {
                    strText = strText.replaceAll(executor.value, "");
                    break;
                }
                default:
                    break;
            }
        });

        return strText;
    };
}

export default new SkuPropertiesFilterStore();
